import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-0 px-4">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 bg-red-600/20 rounded-xl flex items-center justify-center text-red-400 font-bold text-xl mx-auto mb-4">
              !
            </div>
            <h1 className="text-lg font-semibold text-white mb-2">Something went wrong</h1>
            <p className="text-sm text-gray-400 mb-4 leading-relaxed">
              The application encountered an unexpected error. This is usually temporary and can be resolved by refreshing.
            </p>
            <div className="bg-surface-2 border border-surface-4 rounded-lg p-3 mb-4 text-left">
              <p className="text-xs font-mono text-red-300 break-all">
                {this.state.error.message}
              </p>
            </div>
            <button
              className="btn-primary text-sm px-6 py-2.5"
              onClick={() => window.location.reload()}
            >
              Reload Application
            </button>
            <p className="text-xs text-gray-600 mt-4">
              If the problem persists, contact support and include the error message above.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
